import { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './Input';

/**
 * SearchInput — the standard search box used across list pages and tabs.
 *
 * Bundles the affordances every search field should have so they stay
 * consistent app-wide:
 *   - leading search icon
 *   - a clear (x) button once there's a value
 *   - a "/" keyboard shortcut that focuses the box from anywhere on the page
 *     (ignored while typing in another field), with a visible kbd hint
 *   - Esc to clear and blur while focused
 *
 * It is a controlled input: the parent owns the value. For server-backed
 * searches, debounce the value before putting it in the query key (see
 * useDebouncedValue) — this component intentionally doesn't debounce so the
 * typed text stays instant.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  shortcut = '/',
  className,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Key that focuses the box from anywhere on the page. Set '' to disable. */
  shortcut?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!shortcut) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable);
      if (e.key === shortcut && !typing) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcut]);

  return (
    <Input
      ref={ref}
      type="search"
      aria-label={ariaLabel ?? placeholder}
      className={className}
      leftIcon={<Search className="w-4 h-4" />}
      rightIcon={
        value ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              onChange('');
              ref.current?.focus();
            }}
            className="text-text-muted hover:text-text-primary"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : shortcut ? (
          <kbd className="hidden sm:inline-flex items-center rounded border border-glass-hairline bg-white/60 px-1.5 text-micro text-text-muted">
            {shortcut}
          </kbd>
        ) : undefined
      }
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && value) {
          onChange('');
          ref.current?.blur();
        }
      }}
    />
  );
}
